import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axiosInstance from '@/utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, User, Calendar, Building2, Check, X, Printer, Phone, WifiOff, Plus, FileText } from 'lucide-react';
import MedicationGroupByRoute, { createEmptyMedication } from './MedicationGroupByRoute';
import PrescriptionBlock, { createEmptyPrescription } from './PrescriptionBlock';
import OfficialPrescription from './OfficialPrescription';
import DateInput from './DateInput';
import PhoneInput from './PhoneInput';
import { useOfflinePatient } from '@/hooks/useOfflinePatient';
import { useOfflinePrescription } from '@/hooks/useOfflinePrescription';

export default function NewPatientModal({ open, onClose, onSuccess, healthUnits = [] }) {
  const [nome, setNome] = useState('');
  const [dataNascimento, setDataNascimento] = useState('');
  const [unitId, setUnitId] = useState('');
  const [telefone, setTelefone] = useState(''); // Novo campo telefone
  const [loading, setLoading] = useState(false);
  
  // Hooks offline
  const { createPatient, checkHomonym, isOnline, isOfflineReady } = useOfflinePatient();
  const { createPrescription, canCreateFinalPrescription } = useOfflinePrescription();
  
  // Homonym check states
  const [homonymMatches, setHomonymMatches] = useState([]);
  const [showHomonymAlert, setShowHomonymAlert] = useState(false);
  const [homonymCheckDone, setHomonymCheckDone] = useState(false);
  const [checkingHomonym, setCheckingHomonym] = useState(false);
  const [printAfterCreate, setPrintAfterCreate] = useState(false);
  const [printData, setPrintData] = useState(null);
  const debounceRef = useRef(null);
  
  // Estado para múltiplas receitas
  const [prescriptions, setPrescriptions] = useState([createEmptyPrescription()]);
  const [collapsedPrescriptions, setCollapsedPrescriptions] = useState({});
  
  // Backwards compatibility: manter medicationGroups para código legado
  const medicationGroups = prescriptions[0]?.medicationGroups || [{ via: 'ORAL', medicamentos: [createEmptyMedication()] }];

  // Check for homonyms when name changes
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Reset states when name is cleared
    if (!nome || nome.length < 3) {
      setHomonymMatches([]);
      setShowHomonymAlert(false);
      setHomonymCheckDone(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      await checkHomonymHandler(nome);
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [nome]);

  const checkHomonymHandler = async (nameToCheck) => {
    if (!nameToCheck || nameToCheck.length < 3) return;
    
    setCheckingHomonym(true);
    try {
      // Usar hook que funciona online e offline
      const result = await checkHomonym(nameToCheck);
      
      if (result.has_homonym) {
        setHomonymMatches(result.matches);
        setShowHomonymAlert(true);
        setHomonymCheckDone(false); // User needs to confirm
      } else {
        setHomonymMatches([]);
        setShowHomonymAlert(false);
        setHomonymCheckDone(true); // No homonyms, can proceed
      }
    } catch (error) {
      console.error('Erro ao verificar homônimos:', error);
      setHomonymCheckDone(true); // On error, allow to proceed
    } finally {
      setCheckingHomonym(false);
    }
  };

  // Check if the new patient is different enough from homonyms
  const isDifferentFromHomonyms = () => {
    if (homonymMatches.length === 0) return true;
    
    for (const match of homonymMatches) {
      // If same birth date AND same unit, it's a duplicate
      const sameBirthDate = match.data_nascimento === dataNascimento || 
        (match.data_nascimento === 'N/A' && !dataNascimento) ||
        (match.data_nascimento === '01/01/1970' && !dataNascimento);
      
      const sameUnit = match.unit_id === (unitId === 'none' ? null : unitId || null);
      
      if (sameBirthDate && sameUnit) {
        return false;
      }
    }
    return true;
  };

  const confirmHomonymDifferentiation = () => {
    if (!dataNascimento && !unitId) {
      toast.error('Preencha a data de nascimento ou selecione uma unidade de saúde para diferenciar o paciente homônimo');
      return;
    }
    
    if (!isDifferentFromHomonyms()) {
      toast.error('Já existe um paciente com mesmo nome, data de nascimento e unidade. Verifique os dados.');
      return;
    }
    
    setHomonymCheckDone(true);
    setShowHomonymAlert(false);
    toast.success('Paciente diferenciado com sucesso! Pode prosseguir com o cadastro.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nome) {
      toast.error('Preencha o nome do paciente');
      return;
    }

    // Validar telefone se preenchido (deve estar completo)
    if (telefone && telefone.length !== 15) {
      toast.error('Telefone deve estar no formato (DD) 99999-9999');
      return;
    }

    // Check if homonym verification is needed
    if (homonymMatches.length > 0 && !homonymCheckDone) {
      toast.error('Confirme a diferenciação do paciente homônimo antes de prosseguir');
      setShowHomonymAlert(true);
      return;
    }

    // Validar que cada receita tem pelo menos um medicamento válido
    const validPrescriptions = prescriptions.filter(rx => 
      rx.medicationGroups?.some(group => 
        group.medicamentos?.some(med => med.name?.trim())
      )
    );
    
    if (validPrescriptions.length === 0) {
      toast.error('Adicione pelo menos um medicamento em alguma receita');
      return;
    }

    setLoading(true);
    try {
      // 1. Criar o paciente (funciona online e offline)
      const patientResult = await createPatient({
        nome,
        data_nascimento: dataNascimento || '01/01/1970',
        unit_id: unitId === 'none' ? null : unitId || null,
        telefone: telefone || null
      });

      if (!patientResult.success) {
        throw new Error('Erro ao criar paciente');
      }

      const createdPatient = patientResult.patient;
      const isPatientLocal = patientResult.isLocal;

      // 2. Criar TODAS as receitas válidas
      const createdPrescriptions = [];
      let anyPrescriptionLocal = false;

      for (const rx of validPrescriptions) {
        const groupsWithMeds = (rx.medicationGroups || [])
          .map(group => ({
            via: `USO ${group.via}`,
            medicamentos: group.medicamentos.filter(med => med.name?.trim())
          }))
          .filter(group => group.medicamentos.length > 0);
        
        if (groupsWithMeds.length === 0) continue;

        // Flatten all medications with their via info
        const allMedicamentos = [];
        for (const group of groupsWithMeds) {
          for (const med of group.medicamentos) {
            allMedicamentos.push({
              ...med,
              via: group.via
            });
          }
        }
        
        // Determine uso: if multiple vias, list them; otherwise single via
        const uniqueVias = [...new Set(groupsWithMeds.map(g => g.via))];
        const uso = uniqueVias.length === 1 ? uniqueVias[0] : uniqueVias.join(' / ');
        
        // Criar receita (funciona online e offline)
        const prescResult = await createPrescription({
          medicamentos: allMedicamentos,
          uso: uso,
          name: rx.name || '', // Nome personalizado da receita
          medication_groups: groupsWithMeds
        }, createdPatient.id);
        
        if (prescResult.prescription) {
          createdPrescriptions.push(prescResult.prescription);
          if (prescResult.isLocal) anyPrescriptionLocal = true;
        }
      }

      // Mensagem de sucesso baseada no modo (online/offline)
      if (isPatientLocal || anyPrescriptionLocal) {
        toast.success('Dados salvos localmente!', {
          description: `${createdPrescriptions.length} receita(s) serão sincronizadas quando houver conexão.`,
          duration: 4000
        });
      } else {
        toast.success(`Paciente e ${createdPrescriptions.length} receita(s) criados com sucesso!`);
      }
      
      // Se marcou para imprimir e está online (receitas não são rascunhos)
      if (printAfterCreate && createdPrescriptions.length > 0 && !anyPrescriptionLocal) {
        const printPatient = {
          nome: nome.toUpperCase(),
          data_nascimento: dataNascimento || '01/01/1970'
        };
        
        setPrintData({
          patient: printPatient,
          prescriptions: createdPrescriptions // Agora é um array de receitas
        });
        
        // Imprimir após um pequeno delay para renderizar
        setTimeout(() => {
          window.print();
          // Limpar dados de impressão após imprimir
          setTimeout(() => {
            setPrintData(null);
            onSuccess();
            handleReset();
            onClose();
          }, 500);
        }, 200);
      } else if (printAfterCreate && anyPrescriptionLocal) {
        // Receitas são rascunhos offline - não pode imprimir
        toast.warning('Impressão não disponível offline', {
          description: 'As receitas serão sincronizadas quando houver conexão. Depois você poderá imprimi-las.',
          duration: 5000
        });
        onSuccess();
        handleReset();
        onClose();
      } else {
        onSuccess();
        handleReset();
        onClose();
      }
    } catch (error) {
      console.error('Erro ao criar paciente:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro ao criar paciente e receita';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setNome('');
    setDataNascimento('');
    setUnitId('');
    setTelefone('');  // Limpar telefone
    setHomonymMatches([]);
    setShowHomonymAlert(false);
    setHomonymCheckDone(false);
    setPrintAfterCreate(false);
    setPrintData(null);
    setPrescriptions([createEmptyPrescription()]);
    setCollapsedPrescriptions({});
  };

  // Funções para gerenciar múltiplas receitas
  const addPrescription = () => {
    setPrescriptions([...prescriptions, createEmptyPrescription()]);
  };

  const removePrescription = (index) => {
    if (prescriptions.length > 1) {
      setPrescriptions(prescriptions.filter((_, i) => i !== index));
    }
  };

  const updatePrescription = (index, updatedRx) => {
    const newPrescriptions = [...prescriptions];
    newPrescriptions[index] = updatedRx;
    setPrescriptions(newPrescriptions);
  };

  const togglePrescriptionCollapse = (index) => {
    setCollapsedPrescriptions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getUnitName = (unitId) => {
    const unit = healthUnits.find(u => u.id === unitId);
    return unit ? unit.nome : 'Sem unidade';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Indicador de modo offline */}
        {!isOnline && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <WifiOff className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">Modo Offline</p>
              <p className="text-xs text-amber-600">Dados serão salvos localmente e sincronizados quando houver conexão.</p>
            </div>
          </div>
        )}
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Novo Paciente e Receita</DialogTitle>
          <DialogDescription>
            Cadastre um novo paciente com suas informações e medicamentos. 
            Você pode adicionar medicamentos de diferentes vias de administração.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dados do Paciente */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <h3 className="font-semibold text-gray-700">Dados do Paciente</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome" className="text-sm font-medium mb-2 block">
                  Nome do Paciente *
                </Label>
                <div className="relative">
                  <Input
                    id="nome"
                    data-testid="patient-name-input"
                    value={nome}
                    onChange={(e) => setNome(e.target.value.toUpperCase())}
                    placeholder="NOME COMPLETO"
                    className={`bg-white border-gray-200 focus:border-primary/30 focus:bg-white transition-all rounded-lg h-12 ${
                      showHomonymAlert ? 'border-amber-500 focus:border-amber-500' : ''
                    }`}
                    required
                  />
                  {checkingHomonym && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {homonymCheckDone && homonymMatches.length > 0 && (
                    <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="dataNascimento" className="text-sm font-medium mb-2 block">
                  Data de Nascimento {showHomonymAlert && <span className="text-amber-600">*</span>}
                </Label>
                <DateInput
                  id="dataNascimento"
                  data-testid="patient-birth-date-input"
                  value={dataNascimento}
                  onChange={setDataNascimento}
                  className={`bg-white border-gray-200 focus:border-primary/30 focus:bg-white transition-all rounded-lg h-12 ${
                    showHomonymAlert && !dataNascimento ? 'border-amber-500 ring-2 ring-amber-200' : ''
                  }`}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="unit" className="text-sm font-medium mb-2 block">
                Unidade de Saúde {showHomonymAlert && <span className="text-amber-600">*</span>}
              </Label>
              <Select value={unitId} onValueChange={setUnitId}>
                <SelectTrigger 
                  className={`bg-white border-gray-200 focus:border-primary/30 focus:bg-white transition-all rounded-lg h-12 ${
                    showHomonymAlert && !unitId ? 'border-amber-500 ring-2 ring-amber-200' : ''
                  }`}
                  data-testid="patient-unit-select"
                >
                  <SelectValue placeholder="Selecione a unidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma unidade</SelectItem>
                  {healthUnits.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Campo de Telefone com máscara */}
            <div>
              <Label htmlFor="telefone" className="text-sm font-medium mb-2 block">
                <Phone className="w-4 h-4 inline mr-1" />
                Telefone (WhatsApp)
              </Label>
              <PhoneInput
                id="telefone"
                data-testid="patient-phone-input"
                value={telefone}
                onChange={setTelefone}
                className="bg-white border-gray-200 focus:border-primary/30 focus:bg-white transition-all rounded-lg h-12"
              />
              <p className="text-xs text-gray-500 mt-1">
                Formato obrigatório: (DD) 99999-9999 - Para receber lembretes de renovação
              </p>
            </div>
          </div>

          {/* Homonym Alert */}
          {showHomonymAlert && homonymMatches.length > 0 && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-800">
                    Alerta de Paciente Homônimo
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    {homonymMatches.length === 1 
                      ? 'Foi encontrado 1 paciente com o mesmo nome cadastrado.'
                      : `Foram encontrados ${homonymMatches.length} pacientes com o mesmo nome cadastrados.`
                    } Para diferenciar, preencha a <strong>data de nascimento</strong> ou selecione a <strong>unidade de saúde</strong>.
                  </p>
                </div>
              </div>

              {/* Lista de homônimos existentes */}
              <div className="space-y-2 mt-3">
                <p className="text-xs font-medium text-amber-800 uppercase">Pacientes existentes com este nome:</p>
                {homonymMatches.map((match, index) => (
                  <div 
                    key={match.id}
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-amber-100"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{match.nome}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Nascimento: {match.data_nascimento || 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {match.unit_name || 'Sem unidade'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Confirmation button */}
              <div className="flex gap-2 mt-4">
                <Button
                  type="button"
                  onClick={confirmHomonymDifferentiation}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={!dataNascimento && !unitId}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Confirmar Diferenciação
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowHomonymAlert(false);
                    setNome('');
                  }}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar e Limpar
                </Button>
              </div>
            </div>
          )}

          {/* Medicamentos agrupados por via - Sistema de Múltiplas Receitas */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Receitas ({prescriptions.length})
              </h3>
              <Button
                type="button"
                onClick={addPrescription}
                variant="outline"
                size="sm"
                className="text-primary border-primary/30 hover:bg-primary/5"
                data-testid="add-prescription-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Nova Receita
              </Button>
            </div>
            
            {/* Lista de blocos de receitas */}
            <div className="space-y-4">
              {prescriptions.map((rx, index) => (
                <PrescriptionBlock
                  key={index}
                  prescription={rx}
                  index={index}
                  onUpdate={(updated) => updatePrescription(index, updated)}
                  onRemove={() => removePrescription(index)}
                  canRemove={prescriptions.length > 1}
                  collapsed={collapsedPrescriptions[index]}
                  onToggleCollapse={() => togglePrescriptionCollapse(index)}
                />
              ))}
            </div>
          </div>

          {/* Opção de impressão */}
          <div className="flex items-center gap-3 p-3 bg-teal-50 border border-teal-200 rounded-lg">
            <input
              type="checkbox"
              id="printAfterCreate"
              checked={printAfterCreate}
              onChange={(e) => setPrintAfterCreate(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <label htmlFor="printAfterCreate" className="flex items-center gap-2 text-sm font-medium text-teal-800 cursor-pointer">
              <Printer className="w-4 h-4" />
              Imprimir receitas após cadastro
            </label>
          </div>

          {/* Botões de ação */}
          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="rounded-full px-6"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid="submit-patient-button"
              disabled={loading || (homonymMatches.length > 0 && !homonymCheckDone)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-300 rounded-full px-6"
            >
              {loading ? 'Salvando...' : printAfterCreate ? 'Salvar e Imprimir' : 'Salvar Paciente'}
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* Portal para impressão de múltiplas receitas */}
      {printData && createPortal(
        <div className="prescription-print-container" style={{ display: 'none' }}>
          {(printData.prescriptions || [printData.prescription]).map((rx, rxIndex) => (
            <div key={rxIndex}>
              {/* Via 1 */}
              <div style={{ pageBreakAfter: 'always' }}>
                <OfficialPrescription
                  patient={printData.patient}
                  prescription={rx}
                  via={1}
                />
              </div>
              {/* Via 2 */}
              <div style={{ pageBreakAfter: rxIndex < (printData.prescriptions?.length || 1) - 1 ? 'always' : 'auto' }}>
                <OfficialPrescription
                  patient={printData.patient}
                  prescription={rx}
                  via={2}
                />
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}

      {/* Estilos de impressão */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .prescription-print-container,
          .prescription-print-container * {
            visibility: visible;
          }
          .prescription-print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </Dialog>
  );
}
